import { cn } from "@/utils/cn";
import { IonButton } from "@ionic/react";
import { memo, useCallback, useRef, useState } from "react";

interface HoldToConfirmButtonProps {
  label?: string;
  pressingLabel?: string;
  pressDuration?: number;
  onComplete: () => void;
  disabled?: boolean;
  className?: string;
  expand?: "block" | "full";
  type?: "button" | "submit" | "reset";
}

const HoldToConfirmButton = memo(function HoldToConfirmButton({
  label = "Hold to Confirm",
  pressingLabel,
  pressDuration = 5000,
  onComplete,
  disabled = false,
  className = "",
  expand = "block",
  type = "button",
}: HoldToConfirmButtonProps) {
  const [pressProgress, setPressProgress] = useState(0);
  const [isPressed, setIsPressed] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pressStartTimeRef = useRef<number | null>(null);

  const handlePressStart = useCallback(() => {
    if (disabled) return;

    setIsPressed(true);
    pressStartTimeRef.current = Date.now();

    const updateProgress = () => {
      if (pressStartTimeRef.current) {
        const elapsed = Date.now() - pressStartTimeRef.current;
        const progress = Math.min((elapsed / pressDuration) * 100, 100);
        setPressProgress(progress);

        if (progress >= 100) {
          handlePressComplete();
        }
      }
    };

    updateProgress();
    intervalRef.current = setInterval(updateProgress, 1000);
  }, [disabled, pressDuration]);

  const handlePressEnd = useCallback(() => {
    if (pressProgress < 100) {
      setIsPressed(false);
      setPressProgress(0);
      pressStartTimeRef.current = null;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [pressProgress]);

  const handlePressComplete = useCallback(() => {
    setIsPressed(false);
    setPressProgress(0);
    pressStartTimeRef.current = null;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    onComplete();
  }, [onComplete]);

  const remainingSeconds = Math.ceil(
    (pressDuration - (pressProgress / 100) * pressDuration) / 1000,
  );

  return (
    <div className="relative w-full">
      <IonButton
        className={cn(
          "relative h-16 overflow-hidden font-semibold transition-all duration-200",
          isPressed && "scale-95",
          className,
        )}
        color="danger"
        expand={expand}
        type={type}
        disabled={disabled}
        shape="round"
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressEnd}
      >
        <span className="relative z-10">
          {isPressed ? pressingLabel || `Hold for ${remainingSeconds}s` : label}
        </span>
      </IonButton>
    </div>
  );
});

export default HoldToConfirmButton;
